import { useState } from "react";
import { bodyOf, type Email, type JmapClient } from "../lib/jmap";
import { Attachments } from "./Attachments";
import { Icon } from "./Icon";
import { MessageBody } from "./MessageBody";
import { formatTime } from "../lib/settings";

/**
 * Reading pane, markup copied from the design's `renderRead()` —
 * h1 > .msg(.msg-h(.av,.who,.when), .msg-b) > .reply-bar. The design's
 * `data-i="0"` on `.msg` always implied several of them; a conversation renders
 * one block per message.
 *
 * Older messages start collapsed. Opening a long thread and having to scroll
 * past everything you have already read to reach the new reply is the thing
 * that makes threaded clients annoying, so the newest is expanded and the rest
 * are one click away.
 *
 * The prototype injects sender HTML with innerHTML, which is only safe because
 * its data is seeded. Real mail is untrusted; see MessageBody for how it is
 * contained.
 */
function initialsOf(email: Email): string {
  const name = email.from?.[0]?.name ?? email.from?.[0]?.email ?? "?";
  const parts = name.replace(/[<>"]/g, "").trim().split(/[\s.@]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

const AVATAR_COLOURS = ["#4F46E5", "#0EA5E9", "#059669", "#B45309", "#DC2626", "#7C3AED", "#DB2777"];
function colourFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

export function Reader({
  client, messages, newestOnTop, replyDefault, hour24, alwaysShowImages, onBack, onArchive, onDelete, onSpam, onUnread, onReply,
}: {
  client: JmapClient;
  /** The conversation, newest first. Empty when nothing is open. */
  messages: Email[];
  /** Outlook's "Newest message on top". */
  newestOnTop: boolean;
  /** Which action the primary Reply button performs. */
  replyDefault: "reply" | "replyAll";
  /** 24-hour clock, from Settings. */
  hour24: boolean;
  alwaysShowImages: boolean;
  onBack: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onSpam: () => void;
  onUnread: () => void;
  onReply: (e: Email, mode: "reply" | "replyAll" | "forward") => void;
}) {
  // Ids the reader has been asked to expand, on top of the newest.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const open = messages.length > 0;
  // `messages` is newest-first. Outlook defaults to reading downward — oldest
  // at the top, the new reply where the eye lands — but offers the reverse.
  const ordered = newestOnTop ? messages : [...messages].reverse();
  const newest = messages[0];

  return (
    <section className="read" data-screen-label="Reading pane">
      <div className="read-tools" style={{ visibility: open ? "visible" : "hidden" }}>
        <button className="ibtn back" title="Back" onClick={onBack}><Icon name="arrow-left" /></button>
        <button className="ibtn" title="Archive (e)" onClick={onArchive}><Icon name="archive" /></button>
        <button className="ibtn" title="Delete (#)" onClick={onDelete}><Icon name="trash-2" /></button>
        <button className="ibtn" title="Report spam" onClick={onSpam}><Icon name="shield-alert" /></button>
        <span className="sp" />
        <button className="ibtn" title="Mark unread (u)" onClick={onUnread}><Icon name="mail" /></button>
      </div>

      <div className="read-body" id="readBody">
        {!open ? (
          <div className="empty" style={{ height: "100%" }}>
            <Icon name="mail-open" />
            <p>
              Select a conversation to read it here.
              <br />
              Press <b>?</b> for keyboard shortcuts.
            </p>
          </div>
        ) : (
          <>
            <h1>
              {newest.subject || "(no subject)"}
              {messages.length > 1 && (
                <span style={{ font: "500 13px/1 Inter", color: "var(--tx3)", marginLeft: 10 }}>
                  {messages.length} messages
                </span>
              )}
            </h1>

            {ordered.map((email, i) => {
              const isNewest = email.id === newest.id;
              const isOpen = isNewest || expanded.has(email.id);
              const body = bodyOf(email);
              const sender = email.from?.[0];
              return (
                <div className="msg" data-i={i} key={email.id}>
                  <div
                    className="msg-h"
                    onClick={() => {
                      if (isNewest) return;
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(email.id)) next.delete(email.id);
                        else next.add(email.id);
                        return next;
                      });
                    }}
                    role={isNewest ? undefined : "button"}
                    tabIndex={isNewest ? undefined : 0}
                    aria-expanded={isNewest ? undefined : isOpen}
                    style={{ cursor: isNewest ? "default" : "pointer" }}
                  >
                    <div className="av" style={{ background: colourFor(sender?.email ?? email.id) }}>
                      {initialsOf(email)}
                    </div>
                    <div className="who">
                      <b>{sender?.name ?? sender?.email ?? "Unknown"}</b>
                      <span>{isOpen ? sender?.email : email.preview}</span>
                    </div>
                    <div className="when">
                      {new Date(email.receivedAt).toLocaleDateString()}{" "}
                      {formatTime(new Date(email.receivedAt), hour24)}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="msg-b">
                      <MessageBody
                        // Keyed by message id so the "show images" opt-in never
                        // carries from one message to another.
                        key={email.id}
                        client={client}
                        email={email}
                        html={body.html}
                        text={body.text}
                        preview={email.preview}
                        alwaysAllowRemote={alwaysShowImages}
                      />
                      <Attachments client={client} email={email} />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="reply-bar">
              <button className="pill pri" onClick={() => onReply(newest, replyDefault)}>
                <Icon name="reply" />Reply
              </button>
              <button className="pill" onClick={() => onReply(newest, "replyAll")}>
                <Icon name="reply-all" />Reply all
              </button>
              <button className="pill" onClick={() => onReply(newest, "forward")}>
                <Icon name="forward" />Forward
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
